import React, { useState, useMemo } from 'react';
import './student-exams.scss';
import { ExamType, StudentExamsProps } from './student-exams.types';
import { ExamTypeResponse } from '@dopingtech/live_expert_bff_api_ts';
import Checkbox from '@/components/checkbox';
import Dropdown from '@/components/dropdown';
import Pagination from '@/components/pagination';
import LoadingSpinner from '@/components/loading-spinner';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import ExamCard from '@/components/student-exams/components/ExamCard';
import { useSizes } from "@/utility/responsive.ts";
import AddExamModal from '@/components/student-exams/components/AddExamModal';

const ITEMS_PER_PAGE = 10;

const FILTER_OPTIONS: { id: ExamType; name: string }[] = [
	{ id: 'doping', name: 'Doping Hafıza Denemeleri' },
	{ id: 'other', name: 'Diğer Denemeler' }
];

const StudentExams: React.FC<StudentExamsProps> = ({ studentId, gradeId }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [activeFilters, setActiveFilters] = useState<Set<ExamType>>(new Set(['doping', 'other']));
	const [selectedBranch, setSelectedBranch] = useState<ExamTypeResponse | null>(null);
	const [isAddExamModalOpen, setIsAddExamModalOpen] = useState(false);
    const { isMobile } = useSizes();

	const { StudentExamControllerApi } = useApi();

	const { data: examTypeOptions } = useQueryProvider(
		[QUERY_KEYS.findExamTypeByGrade, studentId, gradeId],
		() => StudentExamControllerApi.findExamTypeByGrade(studentId, gradeId),
		{
			select: ({ data }) => data?.data ?? [],
			enabled: !!studentId && !!gradeId,
			refetchOnWindowFocus: false,
		}
	);

	const { data: examResponse, isLoading } = useQueryProvider(
		[QUERY_KEYS.getStudentExams, studentId, currentPage, selectedBranch?.id],
		() => StudentExamControllerApi.getStudentExams(studentId, gradeId, selectedBranch?.id, currentPage - 1, ITEMS_PER_PAGE),
		{
			select: ({ data }) => data?.data,
			enabled: !!studentId,
			refetchOnWindowFocus: false,
		}
	);

	const exams = useMemo(() => (examResponse?.content ?? []).map(item => ({
		...item,
		isDopingExam: true,
		totalNetAnswer: item.totalNetAnswer !== undefined && item.totalNetAnswer !== null ? item.totalNetAnswer.toFixed(2).replace('.', ',') : undefined,
		point: item.point !== undefined && item.point !== null ? item.point.toLocaleString('tr-TR') : undefined,
		rank: item.rank !== undefined && item.rank !== null ? Number(item.rank).toLocaleString('tr-TR') : undefined,
		totalParticipant: item.totalParticipant !== undefined && item.totalParticipant !== null ? item.totalParticipant.toLocaleString('tr-TR') : undefined,
	})), [examResponse]);

	const totalPages = examResponse?.totalPages ?? 0;

	const toggleFilter = (type: ExamType) => {
		setActiveFilters(prev => {
			const next = new Set(prev);
			if (next.has(type)) {
				next.delete(type);
			} else {
				next.add(type);
			}
			return next;
		});
		setCurrentPage(1);
	};

	const filteredExams = useMemo(() => {
		return exams.filter(exam => activeFilters.has(exam.isDopingExam ? 'doping' : 'other'));
	}, [exams, activeFilters]);

	return (
		<>
            <div className="student-exams">
                <div className="student-exams__header">
                    <h3 className="title">Denemeler</h3>
                    <If condition={false}>
                        <button onClick={() => setIsAddExamModalOpen(true)} className="button-add-exam">
                            <i className="icons-add-circle"></i>
                            <span className="label">
                                Deneme Sınavı Ekle
                            </span>
                        </button>
                    </If>
                </div>
                <div className="student-exams__toolbar">
                    <div className="student-exams__filters">
                        <If condition={false}>
                                {FILTER_OPTIONS.map(option => (
                                    <Checkbox
                                        key={option.id}
                                        label={
                                            <span className="student-exams__filter-label">
                                                {option.name}
                                                <span className={`student-exams__filter-dot student-exams__filter-dot--${option.id}`} />
                                            </span>
                                        }
                                        checked={activeFilters.has(option.id)}
                                        onChange={() => toggleFilter(option.id)}
                                        size="small"
                                    />
                                ))}
                        </If>
                    </div>

                    <Dropdown
                        options={examTypeOptions ?? []}
                        selectedOption={selectedBranch}
                        onOptionSelect={(option) => { setSelectedBranch(option); setCurrentPage(1); }}
                        size="small"
                        menuAlign={isMobile ? 'center' : 'right'}
                        fullWidth={isMobile}
                        placeholder="Sınav Tipi"
                    />
                </div>

                <div className="student-exams__list">
                    <If condition={isLoading}>
                        <LoadingSpinner loadingText={'Yükleniyor'} />
                    </If>
                    <Choose>
                        <When condition={!isLoading && filteredExams.length === 0}>
                            <div className="student-exams__empty-state">
                                <p>Bu öğrenci için henüz deneme kaydı bulunmamaktadır.</p>
                            </div>
                        </When>
                        <Otherwise>
                            {filteredExams.map(exam => (
                                <ExamCard key={exam.examId} data={exam} studentId={studentId} />
                            ))}
                        </Otherwise>
                    </Choose>
                </div>

                <If condition={totalPages > 1}>
                    <div className="student-exams__pagination">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </If>
            </div>

            <AddExamModal
                isOpen={isAddExamModalOpen}
                onClose={() => setIsAddExamModalOpen(false)}
            />
		</>
	);
};

export default StudentExams;
